import React from "react";

const MethodologyBackground: React.FC = () => (
  <div className="space-y-6 text-sm md:text-base leading-relaxed text-[#380C1E]/90 font-light">
    <section>
      <h2 className="font-serif text-2xl font-normal text-[#380C1E] pt-6 pb-2 border-t border-[#E8DEC8]">
        2. Background: how burnout has been measured
      </h2>
      <p className="mb-3">
        The term burnout entered the professional literature through Herbert
        Freudenberger's 1974 description of exhaustion among volunteer staff in
        a free clinic (Freudenberger, 1974). Christina Maslach and Susan Jackson
        operationalized it with the Maslach Burnout Inventory (MBI), which
        measures three dimensions: emotional exhaustion, depersonalization
        (later generalized as cynicism), and reduced personal accomplishment
        (Maslach and Jackson, 1981). The MBI remains the most widely used
        measure, and its three-factor structure has been replicated across large
        international samples (Poghosyan, Aiken, and Sloane, 2009).
      </p>
      <p className="mb-3">
        Two later instruments matter for the present design. The Oldenburg
        Burnout Inventory (OLBI) reduced the construct to two dimensions,
        exhaustion and disengagement, and deliberately included positively and
        negatively worded items to reduce response bias (Demerouti, Bakker,
        Vardakou, and Kantas, 2003; Reis, Xanthopoulou, and Tsaousis, 2015). The
        Burnout Assessment Tool (BAT) reframed burnout around four core
        dimensions, exhaustion, mental distance, cognitive impairment, and
        emotional impairment, and was built in part to address the MBI's
        limitations as a screening instrument (Schaufeli, Desart, and De Witte,
        2020).
      </p>
      <p className="mb-3">
        In 2019 the World Health Organization included burn-out in the eleventh
        revision of the International Classification of Diseases as an
        occupational phenomenon (code QD85), defined by energy depletion,
        increased mental distance or negativism toward one's job, and reduced
        professional efficacy. The WHO entry states that burn-out is not
        classified as a medical condition and that it "refers specifically to
        phenomena in the occupational context and should not be applied to
        describe experiences in other areas of life" (World Health Organization,
        2019).
      </p>
      <p className="mb-3">
        That occupational boundary is exactly where the present instrument
        departs from the established literature. Researchers have already
        extended the construct beyond paid work: the Parental Burnout Assessment
        measures exhaustion, contrast with a former parental self, feelings of
        being fed up, and emotional distancing among parents (Roskam, Brianda,
        and Mikolajczak, 2018). Maslach and Leiter's six areas of worklife model
        (workload, control, reward, community, fairness, and values) established
        that burnout is best understood as a mismatch between a person and her
        environment rather than a personal deficiency (Leiter and Maslach, 1999;
        Maslach and Leiter, 1997).
      </p>
      <p>
        Finally, the overlap between burnout and depression is well documented
        and unresolved. Bianchi, Schonfeld, and Laurent's review found
        substantial overlap between the two constructs and cautioned against
        treating burnout as a clearly distinct entity (Bianchi, Schonfeld, and
        Laurent, 2015). This finding shapes the instrument's care language and
        referral rule, described in Section 8.
      </p>
    </section>
  </div>
);

export default MethodologyBackground;
